import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, Plus } from 'lucide-react'
import { validateSummaryTemplate } from '@kfolks/models'
import { Input } from '@/shared/components/atoms/ui/input'
import { Button } from '@/shared/components/atoms/ui/button'
import { useFormBuilderState } from './FormBuilderState'
import { buildPlaceholderValues, renderSummaryPreview } from './summary-template-render'

/**
 * Summary-template authoring input for the visual form builder, per design
 * Q4-style resolution and the form-builder capability spec.
 *
 * Two affordances backed by the **in-progress** `formSchema.fields` (so
 * suggestions stay in sync as the admin renames or adds fields in the same
 * session):
 *
 *  1. **Inline autocomplete** triggered when the admin types `{{` (token)
 *     or `{{#` (section open). Listbox shows every available field id with
 *     its label. Selecting an entry inserts the appropriate completion at
 *     the caret:
 *       - `{{` → inserts `<id>}}`
 *       - `{{#` → inserts `<id>}}{{/<id>}}` and positions the caret inside
 *
 *  2. **"Insert field" picker** — a button next to the input that opens a
 *     flat dropdown of available field ids, for click-driven authoring
 *     when the admin doesn't want to type `{{` first. Insert mode toggle
 *     ({{ vs {{#) controls the inserted shape.
 *
 * Live `validateSummaryTemplate({ formSchema })` runs on every debounced
 * edit (250 ms — same default the rest of the FE uses). Errors render
 * inline beneath the input.
 *
 * Live preview renders the template via `renderSummaryPreview` against
 * placeholder values (one per token), so admins can sanity-check the
 * output without saving + submitting a request. Empty template renders
 * an empty preview — admin sees they need to type something.
 *
 * Saved via the same `PUT /requests/types/:id` endpoint as `formSchema`
 * (task 7.9 — builder-wide save action).
 */
export function SummaryTemplateInput() {
	const { formSchema, summaryTemplate, setSummaryTemplate } = useFormBuilderState()
	const [open, setOpen] = useState<OpenState>({ kind: 'closed' })
	const [pickerOpen, setPickerOpen] = useState(false)
	const [insertMode, setInsertMode] = useState<'token' | 'section'>('token')
	const inputRef = useRef<HTMLInputElement | null>(null)

	const fieldIds = useMemo(() => formSchema.fields.map((f) => f.id), [formSchema.fields])
	const labelById = useMemo(() => {
		const m: Record<string, string> = {}
		for (const f of formSchema.fields) m[f.id] = f.label
		return m
	}, [formSchema.fields])

	// ─── Validation (debounced) ──────────────────────────────────────────────

	const [debouncedTemplate, setDebouncedTemplate] = useState(summaryTemplate ?? '')
	useEffect(() => {
		const handle = window.setTimeout(() => setDebouncedTemplate(summaryTemplate ?? ''), 250)
		return () => window.clearTimeout(handle)
	}, [summaryTemplate])
	const validation = useMemo(
		() =>
			debouncedTemplate.length === 0
				? { valid: true, errors: [] as { path: string; message: string; severity: string }[] }
				: validateSummaryTemplate(debouncedTemplate, { formSchema }),
		[debouncedTemplate, formSchema],
	)

	// ─── Preview ─────────────────────────────────────────────────────────────

	const placeholders = useMemo(() => buildPlaceholderValues(fieldIds), [fieldIds])
	const preview = useMemo(
		() => renderSummaryPreview(summaryTemplate ?? '', { values: placeholders }),
		[summaryTemplate, placeholders],
	)

	// ─── Input + autocomplete handlers ───────────────────────────────────────

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const next = e.target.value
		setSummaryTemplate(next.length === 0 ? null : next)
		updateAutocompleteState(next, e.target.selectionStart ?? next.length, setOpen)
	}

	const handleSelectionChange = () => {
		const el = inputRef.current
		if (!el) return
		updateAutocompleteState(el.value, el.selectionStart ?? el.value.length, setOpen)
	}

	const insertCompletion = (fieldId: string, mode: 'token' | 'section', cursorReplaceFrom: number) => {
		const el = inputRef.current
		if (!el) return
		const value = summaryTemplate ?? ''
		const head = value.slice(0, cursorReplaceFrom)
		const tail = value.slice(el.selectionStart ?? value.length)
		const completion = mode === 'section' ? `${fieldId}}}{{/${fieldId}}}` : `${fieldId}}}`
		const next = head + completion + tail
		setSummaryTemplate(next.length === 0 ? null : next)
		// Reposition caret AFTER React reflects the new value. For token
		// inserts we land right after `}}`; for section inserts we land
		// inside the section so the admin can keep typing the inner content.
		queueMicrotask(() => {
			const caret = mode === 'section' ? head.length + `${fieldId}}}`.length : head.length + completion.length
			el.focus()
			el.setSelectionRange(caret, caret)
		})
		setOpen({ kind: 'closed' })
	}

	// ─── Insert-field picker handlers ────────────────────────────────────────

	const insertFromPicker = (fieldId: string) => {
		const el = inputRef.current
		if (!el) return
		const value = summaryTemplate ?? ''
		const caret = el.selectionStart ?? value.length
		const head = value.slice(0, caret)
		const tail = value.slice(caret)
		const completion = insertMode === 'section' ? `{{#${fieldId}}}{{/${fieldId}}}` : `{{${fieldId}}}`
		const next = head + completion + tail
		setSummaryTemplate(next.length === 0 ? null : next)
		queueMicrotask(() => {
			const newCaret =
				insertMode === 'section' ? head.length + `{{#${fieldId}}}`.length : head.length + completion.length
			el.focus()
			el.setSelectionRange(newCaret, newCaret)
		})
		setPickerOpen(false)
	}

	return (
		<div className="space-y-2" role="region" aria-label="Summary template editor">
			<div className="flex items-end gap-2">
				<div className="relative flex-1">
					<label htmlFor="summary-template-input" className="block space-y-1 text-sm">
						<span className="font-medium text-foreground">Summary template</span>
						<Input
							id="summary-template-input"
							ref={inputRef}
							type="text"
							value={summaryTemplate ?? ''}
							onChange={handleChange}
							onKeyUp={handleSelectionChange}
							onClick={handleSelectionChange}
							placeholder="e.g. {{access_level}} access on {{target_provider}}"
							autoComplete="off"
							spellCheck={false}
							aria-autocomplete="list"
							aria-expanded={open.kind !== 'closed'}
							aria-controls={open.kind !== 'closed' ? 'summary-autocomplete' : undefined}
						/>
					</label>
					{open.kind !== 'closed' ? (
						<AutocompleteList
							open={open}
							fieldIds={fieldIds}
							labelById={labelById}
							onPick={(id) => insertCompletion(id, open.kind, open.replaceFrom)}
						/>
					) : null}
				</div>

				<div className="flex items-center gap-1">
					<select
						value={insertMode}
						onChange={(e) => setInsertMode(e.target.value as 'token' | 'section')}
						aria-label="Insert mode"
						className="h-10 rounded-md border border-input bg-background px-2 text-sm"
					>
						<option value="token">{`{{token}}`}</option>
						<option value="section">{`{{#section}}…{{/section}}`}</option>
					</select>
					<div className="relative">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setPickerOpen((v) => !v)}
							aria-label="Insert field"
							aria-haspopup="listbox"
							aria-expanded={pickerOpen}
						>
							<Plus className="size-4" /> Insert field
						</Button>
						{pickerOpen ? (
							<ul
								role="listbox"
								aria-label="Insert field picker"
								className="absolute right-0 top-full z-10 mt-1 max-h-64 w-64 overflow-auto rounded-md border bg-popover p-1 shadow-md"
							>
								{fieldIds.length === 0 ? (
									<li className="p-2 text-xs text-muted-foreground">No fields yet — add one on the canvas.</li>
								) : (
									fieldIds.map((id) => (
										<li key={id}>
											<button
												type="button"
												onClick={() => insertFromPicker(id)}
												className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm hover:bg-accent"
											>
												<span className="flex-1">{labelById[id] || id}</span>
												<span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
													{id}
												</span>
											</button>
										</li>
									))
								)}
							</ul>
						) : null}
					</div>
				</div>
			</div>

			{validation.errors.length > 0 ? (
				<div
					role="alert"
					className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive"
				>
					{validation.errors.map((err, i) => (
						<div key={i} className="flex items-start gap-2">
							<AlertCircle className="mt-0.5 size-4 shrink-0" />
							<p className="text-xs">{err.message}</p>
						</div>
					))}
				</div>
			) : null}

			<SummaryPreview rendered={preview} hasTemplate={!!summaryTemplate} />
		</div>
	)
}

// ─── Autocomplete dropdown ───────────────────────────────────────────────────

type OpenState =
	| { kind: 'closed' }
	| { kind: 'token'; replaceFrom: number; query: string }
	| { kind: 'section'; replaceFrom: number; query: string }

function AutocompleteList({
	open,
	fieldIds,
	labelById,
	onPick,
}: {
	open: Exclude<OpenState, { kind: 'closed' }>
	fieldIds: ReadonlyArray<string>
	labelById: Record<string, string>
	onPick: (id: string) => void
}) {
	const filtered =
		open.query.length === 0
			? fieldIds
			: fieldIds.filter((id) => {
					const lower = open.query.toLowerCase()
					return id.toLowerCase().includes(lower) || (labelById[id] || '').toLowerCase().includes(lower)
				})

	return (
		<ul
			id="summary-autocomplete"
			role="listbox"
			aria-label="Field id suggestions"
			className="absolute left-0 top-full z-10 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md"
		>
			{filtered.length === 0 ? (
				<li className="p-2 text-xs text-muted-foreground">
					No matching fields {open.query ? `for "${open.query}"` : ''}
				</li>
			) : (
				filtered.map((id) => (
					<li key={id}>
						<button
							type="button"
							onClick={() => onPick(id)}
							className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm hover:bg-accent"
						>
							<span className="flex-1">{labelById[id] || id}</span>
							<span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{id}</span>
						</button>
					</li>
				))
			)}
		</ul>
	)
}

// ─── Live preview ────────────────────────────────────────────────────────────

function SummaryPreview({ rendered, hasTemplate }: { rendered: string; hasTemplate: boolean }) {
	return (
		<div className="rounded-md border bg-muted/30 p-2 text-sm">
			<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Preview</p>
			{hasTemplate ? (
				<p className="mt-1 break-words" data-testid="summary-template-preview">
					{rendered || <span className="italic text-muted-foreground">(empty after substitution)</span>}
				</p>
			) : (
				<p className="mt-1 italic text-muted-foreground">Type a template above to see the rendered preview.</p>
			)}
		</div>
	)
}

// ─── Caret-based autocomplete trigger detection ──────────────────────────────

/**
 * Inspects the text immediately preceding the caret to decide whether to
 * open the autocomplete and in which mode.
 *
 *   - `{{`  followed by zero+ word chars → token mode, query is the chars
 *   - `{{#` followed by zero+ word chars → section mode, query is the chars
 *
 * The first matching un-closed `{{` walking backwards from the caret wins;
 * a closing `}}` between the caret and the last `{{` means the admin
 * already finished a token and we should NOT open. `replaceFrom` is the
 * caret offset where insertion should begin (right after the trigger),
 * so accepting a suggestion replaces only the typed query (not the `{{`
 * itself).
 */
function updateAutocompleteState(value: string, caret: number, setOpen: (s: OpenState) => void) {
	// Walk backwards from caret looking for the most recent unclosed `{{`.
	const head = value.slice(0, caret)
	// If there's a `}}` between any candidate `{{` and the caret, the token
	// is closed and we shouldn't open the dropdown. Cheap detection: split
	// on `}}` and inspect the last segment only.
	const lastSegment = head.split('}}').pop() ?? head
	// In the last segment, find the most recent `{{` (or `{{#`).
	const sectionIdx = lastSegment.lastIndexOf('{{#')
	const tokenIdx = lastSegment.lastIndexOf('{{')
	if (tokenIdx === -1) {
		setOpen({ kind: 'closed' })
		return
	}
	const isSection = sectionIdx !== -1 && sectionIdx === tokenIdx
	const triggerEnd = isSection ? sectionIdx + 3 : tokenIdx + 2
	// Anything between trigger and caret must be a valid token-id prefix
	// (alphanumeric / `_`) — otherwise the admin typed past the autocomplete
	// scope (e.g., a space) and we close.
	const query = lastSegment.slice(triggerEnd)
	if (!/^[a-zA-Z0-9_]*$/.test(query)) {
		setOpen({ kind: 'closed' })
		return
	}
	const baseOffset = head.length - lastSegment.length
	const replaceFrom = baseOffset + triggerEnd
	setOpen(isSection ? { kind: 'section', replaceFrom, query } : { kind: 'token', replaceFrom, query })
}
